import React, { useState } from "react";
import styled from "styled-components";
import { Container } from "react-bootstrap";
import { applyToOpening, trackEvent } from "../../lib/openings";

const Section = styled.section`
  padding: 16px 0 96px;
  background: var(--nd-black);
`;

const Card = styled.form`
  background: var(--nd-surface);
  border: 1px solid var(--nd-border);
  border-radius: 12px;
  padding: 32px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  @media (max-width: 768px) { padding: 24px; }
`;

const Heading = styled.h2`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 28px;
  font-weight: 500;
  color: var(--nd-text-display);
  letter-spacing: -0.01em;
  margin: 0;
`;

const Sub = styled.p`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 14px;
  color: var(--nd-text-secondary);
  line-height: 1.5;
  margin: 0 0 8px;
`;

const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const FieldLabel = styled.span`
  font-family: 'Space Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--nd-text-secondary);
`;

const Input = styled.input`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 15px;
  background: var(--nd-black);
  color: var(--nd-text-display);
  border: 1px solid var(--nd-border-visible);
  border-radius: 8px;
  padding: 12px 14px;
  min-height: 44px;
  &:focus {
    outline: none;
    border-color: var(--nd-text-display);
  }
`;

const Textarea = styled.textarea`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 15px;
  background: var(--nd-black);
  color: var(--nd-text-display);
  border: 1px solid var(--nd-border-visible);
  border-radius: 8px;
  padding: 12px 14px;
  min-height: 120px;
  resize: vertical;
  &:focus {
    outline: none;
    border-color: var(--nd-text-display);
  }
`;

const CharCount = styled.span`
  font-family: 'Space Mono', monospace;
  font-size: 10px;
  color: var(--nd-text-disabled);
  letter-spacing: 0.04em;
  text-align: right;
`;

const Button = styled.button`
  font-family: 'Space Mono', monospace;
  font-size: 13px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  background: var(--nd-accent);
  color: var(--nd-text-display);
  border: none;
  border-radius: 999px;
  padding: 14px 28px;
  min-height: 44px;
  cursor: pointer;
  align-self: flex-start;
  transition: opacity 200ms ease;
  &:hover:not(:disabled) { opacity: 0.85; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const Message = styled.p`
  font-family: 'Space Mono', monospace;
  font-size: 12px;
  letter-spacing: 0.04em;
  margin: 0;
  color: ${(p) => (p.kind === "error" ? "var(--nd-accent)" : "var(--nd-success)")};
`;

const PITCH_MAX = 800;

const isEmail = (s) =>
  typeof s === "string" && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s.trim());

const ApplicationForm = ({ opening }) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [linkedinUrl, setLinkedinUrl] = useState("");
  const [source, setSource] = useState("");
  const [pitch, setPitch] = useState("");
  const [state, setState] = useState("idle"); // idle | busy | done | error
  const [errorMsg, setErrorMsg] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg("");
    if (!name.trim()) { setErrorMsg("Your name is required."); return; }
    if (!isEmail(email)) { setErrorMsg("A valid email is required."); return; }
    setState("busy");
    try {
      await applyToOpening({
        openingId: opening.id,
        openingSlug: opening.slug,
        openingTitle: opening.title,
        name: name.trim(),
        email: email.trim(),
        linkedinUrl: linkedinUrl.trim() || null,
        source: source.trim() || null,
        pitch: pitch.trim().slice(0, PITCH_MAX) || null,
      });
      trackEvent("application_submitted", { opening_id: opening.id, opening_slug: opening.slug });
      setState("done");
    } catch (err) {
      setState("error");
      setErrorMsg((err && err.message) || "Something went wrong. Please try again.");
    }
  };

  if (state === "done") {
    return (
      <Section>
        <Container>
          <Card as="div">
            <Heading>Thanks — application received.</Heading>
            <Sub>
              We will reply within 3 business days. If you don't hear back, the
              email may be in spam — check there first, then reply to this thread.
            </Sub>
          </Card>
        </Container>
      </Section>
    );
  }

  return (
    <Section>
      <Container>
        <Card onSubmit={onSubmit}>
          <Heading>Apply directly</Heading>
          <Sub>
            One screen, no account needed. We read every application — usually
            within 3 business days.
          </Sub>

          <Field>
            <FieldLabel>Full name *</FieldLabel>
            <Input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              autoComplete="name"
              maxLength={100}
            />
          </Field>

          <Field>
            <FieldLabel>Email *</FieldLabel>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              maxLength={200}
            />
          </Field>

          <Field>
            <FieldLabel>LinkedIn URL (optional)</FieldLabel>
            <Input
              type="url"
              value={linkedinUrl}
              onChange={(e) => setLinkedinUrl(e.target.value)}
              placeholder="https://www.linkedin.com/in/…"
              maxLength={300}
            />
          </Field>

          <Field>
            <FieldLabel>How did you find this opening? (optional)</FieldLabel>
            <Input
              type="text"
              value={source}
              onChange={(e) => setSource(e.target.value)}
              placeholder="LinkedIn, referral, Google, etc."
              maxLength={120}
            />
          </Field>

          <Field>
            <FieldLabel>Short pitch — why you (optional, {PITCH_MAX} chars)</FieldLabel>
            <Textarea
              value={pitch}
              onChange={(e) => setPitch(e.target.value.slice(0, PITCH_MAX))}
              maxLength={PITCH_MAX}
              placeholder="What you have shipped, where you got stuck, what you want to learn next."
            />
            <CharCount>{pitch.length} / {PITCH_MAX}</CharCount>
          </Field>

          {errorMsg && <Message kind="error">{errorMsg}</Message>}

          <Button type="submit" disabled={state === "busy"}>
            {state === "busy" ? "Sending…" : "Send application"}
          </Button>
        </Card>
      </Container>
    </Section>
  );
};

export default ApplicationForm;
